import React, { cloneElement, ReactElement, ReactNode, useState } from 'react';
import { FormGuardProvider } from './hook-form/FormGuard';

export type PopoverProps<P extends object> = P & {
  target: HTMLElement;
  handleClose(): void;
};

type PopoverTriggerProps<P extends object> = {
  children: ReactNode;
  context?: P;
  popover: (props: PopoverProps<P>) => ReactElement;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  isOpened?: (opened: boolean) => void;
  handleRightClick?: boolean;
  disabled?: boolean;
  parentAnchorEl?: HTMLElement | null;
};

export const PopoverTrigger = <P extends object>({
  children,
  popover: Popover,
  context,
  onClick,
  isOpened,
  handleRightClick,
  disabled,
  parentAnchorEl,
}: PopoverTriggerProps<P>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(parentAnchorEl || e.currentTarget);
    e.currentTarget.classList.add('active');
    onClick?.(e);
    if (isOpened) isOpened(true);
  };

  const handleClose = () => {
    anchorEl?.classList.remove('active');
    setAnchorEl(null);
    if (isOpened) isOpened(false);
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLElement>) => {
    if (!handleRightClick) return;
    e.preventDefault();
    handleOpen(e);
  };

  if (!Popover) return null;

  return (
    <FormGuardProvider>
      {cloneElement(children as any, {
        onClick: handleOpen,
        onContextMenu: handleContextMenu,
      })}
      {anchorEl && (
        <Popover
          target={anchorEl}
          handleClose={handleClose}
          {...(context as P)}
        />
      )}
    </FormGuardProvider>
  );
};
